// Self-contained enrichment logic for doctor cards used by the search feature.
// Duplicated from bookingWidget to avoid cross-feature dependency.
// Changes to bookingWidget should NOT affect the search feature.

import type { Doctor, DoctorImage, Location } from '@/types/doctor';
import { withoutEmailAddressesInLocation } from '@/lib/consultants/locationPrivacy';

export interface SearchBookingDoctor {
  id: string;
  type: string;
  title: string;
  slug: string;
  fullName: string;
  assistantPhoneNumber: string | null;
  assistantEmailAddresses: string[];
  heading: { raw: string | null };
  description: { raw: string | null };
  image?: DoctorImage | null;
  primaryPrice: string | null;
  secondaryPrice: string | null;
  deposit?: string | null;
  apiId: string | null;
  categoryName: string | null;
  categoryId: string | null;
  duration: string | number | null;
  serviceAccount: boolean;
  extraService?: string | null;
  doctifySlug: string | null;
  primarySpeciality: string | undefined;
  specialities: string[] | undefined;
  typeOfDoctor?: any;
  gender?: string;
  privateLocations?: Location[];
  primaryLocations?: Location[];
}

function inferType(slug: string): string {
  return slug.toLowerCase().includes('clinic') ? 'clinic' : 'doctor';
}

export function mapDoctorToSearchBookingDoctor(
  doctor: Doctor,
): SearchBookingDoctor {
  return {
    id: doctor.slug,
    type: inferType(doctor.slug),
    title: doctor.title ?? doctor.fullName ?? doctor.slug,
    slug: doctor.slug,
    fullName: doctor.fullName ?? doctor.slug,
    assistantPhoneNumber: doctor.assistantPhoneNumber ?? null,
    heading: { raw: doctor.heading ?? null },
    description: { raw: doctor.description ?? null },
    // Avatars are stamped as full CDN URLs in the generated doctor index; keep
    // that URL so the booking widget never depends on a per-route manifest key.
    image: doctor.image ?? null,
    primaryPrice: doctor.primaryPrice ?? doctor.primaryPrice ?? null,
    secondaryPrice: doctor.secondaryPrice ?? null,
    deposit: doctor.deposit ?? null,
    apiId: doctor.apiId ?? null,
    categoryName: doctor.categoryName ?? null,
    categoryId: doctor.categoryId ?? null,
    duration: doctor.duration ?? null,
    serviceAccount: doctor.serviceAccount ?? false,
    doctifySlug: doctor.doctifySlug ?? doctor.slug,
    primarySpeciality: doctor.primarySpeciality,
    specialities: doctor.specialities,
    gender: doctor.gender,
    privateLocations: doctor.privateLocations?.map(
      withoutEmailAddressesInLocation,
    ),
    primaryLocations: doctor.primaryLocations?.map(
      withoutEmailAddressesInLocation,
    ),
  };
}

export function isSearchDoctorBookable(
  mappedDoctor: SearchBookingDoctor,
): boolean {
  if (!mappedDoctor.apiId) return false;
  if (!mappedDoctor.categoryId) return false;
  if (!mappedDoctor.primaryPrice && !mappedDoctor.deposit) return false;
  return true;
}
